import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Eye, EyeOff, Mail, Lock, User, ArrowRight, Building2, Loader2, ArrowLeft } from 'lucide-react';
import { authService } from '../../api/services';
import { FlickeringGrid } from '../../components/ui/flickering-grid';
import logo from '../../assets/logo.svg';

const Login = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isSignUp, setIsSignUp] = useState(searchParams.get('signup') === 'true');
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    organizationName: '',
    email: '',
    password: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = isSignUp
        ? await authService.register({
            firstName: formData.firstName.trim(),
            lastName: formData.lastName.trim(),
            organizationName: formData.organizationName.trim(),
            email: formData.email.trim(),
            password: formData.password,
          })
        : await authService.login({
            email: formData.email.trim(),
            password: formData.password,
          });

      if (response?.data?.success) {
        const { accessToken, refreshToken, user } = response.data.data;
        if (!accessToken) throw new Error("Token d'accès manquant dans la réponse");
        localStorage.setItem('accessToken', accessToken);
        if (refreshToken) localStorage.setItem('refreshToken', refreshToken);
        if (user) localStorage.setItem('user', JSON.stringify(user));
        navigate('/');
      } else {
        setError(response?.data?.error?.message || response?.data?.message || 'Erreur, veuillez réessayer');
      }
    } catch (err) {
      let msg =
        err?.error?.message ||
        err?.response?.data?.error?.message ||
        err?.response?.data?.message ||
        err?.message;

      if (!msg) msg = err?.request ? 'Impossible de contacter le serveur.' : 'Erreur inconnue';
      if (isSignUp && /validation/i.test(msg))
        msg = "Vérifiez vos informations : prénom, nom et entreprise (2 car. min.), email valide et mot de passe d'au moins 8 caractères.";

      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex relative" style={{ background: 'var(--c-primary-dark)' }}>
      {/* Back to landing page */}
      <a
        href={process.env.REACT_APP_LANDING_URL || '/'}
        className="absolute top-4 left-4 z-10 flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all hover:bg-[var(--c-hover)]"
        style={{ color: 'var(--c-text-gray)', textDecoration: 'none' }}
      >
        <ArrowLeft size={15} />
        <span>Accueil</span>
      </a>
      {/* Left panel — decorative */}
      <div className="hidden lg:flex lg:w-[58%] relative overflow-hidden" style={{ background: 'var(--c-primary-card)' }}>
        <FlickeringGrid
          squareSize={4}
          gridGap={6}
          color="#10b981"
          maxOpacity={0.25}
          flickerChance={0.1}
          width={1200}
          height={1000}
          className="absolute inset-0 z-0"
        />
        <div
          className="absolute inset-0 z-0"
          style={{ background: 'linear-gradient(135deg, rgba(var(--c-accent-green-rgb),0.06) 0%, transparent 60%)' }}
        />
        <div className="relative z-10 flex flex-col justify-center items-start p-16">
          <div className="max-w-xl">
            <div className="mb-10">
              <img src={logo} alt="Swift AI" className="h-14" />
            </div>
            <h2 className="text-4xl font-bold mb-5 leading-tight text-ink">
              Transformez votre business avec l'IA
            </h2>
            <p className="text-lg text-label mb-10 leading-relaxed">
              Automatisez vos conversations WhatsApp, gérez vos agents intelligents et boostez votre productivité.
            </p>

            {/* Feature pills */}
            <div className="flex flex-col gap-3">
              {[
                'Agents IA multilingues',
                'Automatisation WhatsApp Business',
                'Gestion commandes & factures',
                'Paiements Mobile Money intégrés',
              ].map((f) => (
                <div
                  key={f}
                  className="flex items-center gap-3 px-4 py-3 rounded-xl"
                  style={{ background: 'rgba(var(--c-accent-green-rgb),0.08)', border: '0.5px solid rgba(var(--c-accent-green-rgb),0.2)' }}
                >
                  <span
                    className="w-2 h-2 rounded-full flex-shrink-0"
                    style={{ background: 'rgb(var(--c-accent-green-rgb))' }}
                  />
                  <span className="text-sm font-medium text-ink">{f}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Right panel — form */}
      <div
        className="w-full lg:w-[42%] flex items-center justify-center p-6 sm:p-10"
        style={{ background: 'var(--c-primary-dark)' }}
      >
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="lg:hidden text-center mb-8">
            <img src={logo} alt="Swift AI" className="h-10 mx-auto" />
          </div>

          {/* Card */}
          <div className="card p-8 rounded-2xl shadow-2xl">
            <div className="mb-7">
              <p className="text-xs font-semibold uppercase tracking-widest mb-1" style={{ color: 'rgb(var(--c-accent-green-rgb))' }}>
                {isSignUp ? 'Inscription' : 'Connexion'}
              </p>
              <h2 className="text-2xl font-bold text-ink">
                {isSignUp ? 'Créer un compte' : 'Bon retour 👋'}
              </h2>
              <p className="text-label text-sm mt-1">
                {isSignUp ? 'Rejoignez Swift AI aujourd\'hui' : 'Connectez-vous à votre dashboard'}
              </p>
            </div>

            {error && (
              <div
                className="flex items-start gap-2 p-3.5 mb-5 rounded-lg text-sm"
                style={{ background: 'rgba(239,68,68,0.08)', border: '0.5px solid rgba(239,68,68,0.4)', color: '#ef4444' }}
              >
                <span className="flex-shrink-0 mt-0.5">✕</span>
                <span>{error}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              {isSignUp && (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-ink text-sm font-medium mb-1.5" htmlFor="firstName">
                        Prénom
                      </label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 text-label" size={16} />
                        <input
                          type="text"
                          id="firstName"
                          name="firstName"
                          value={formData.firstName}
                          onChange={handleChange}
                          className="w-full pl-9 pr-3 py-2.5 rounded-lg text-ink text-sm focus:outline-none focus:ring-2 focus:ring-accent-green transition-all"
                          style={{ background: 'var(--c-primary-dark)', border: '0.5px solid var(--c-border)' }}
                          placeholder="Jean"
                          minLength={2}
                          required={isSignUp}
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-ink text-sm font-medium mb-1.5" htmlFor="lastName">
                        Nom
                      </label>
                      <input
                        type="text"
                        id="lastName"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleChange}
                        className="w-full px-3 py-2.5 rounded-lg text-ink text-sm focus:outline-none focus:ring-2 focus:ring-accent-green transition-all"
                        style={{ background: 'var(--c-primary-dark)', border: '0.5px solid var(--c-border)' }}
                        placeholder="Dupont"
                        minLength={2}
                        required={isSignUp}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-ink text-sm font-medium mb-1.5" htmlFor="organizationName">
                      Nom de l'entreprise
                    </label>
                    <div className="relative">
                      <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 text-label" size={16} />
                      <input
                        type="text"
                        id="organizationName"
                        name="organizationName"
                        value={formData.organizationName}
                        onChange={handleChange}
                        className="w-full pl-9 pr-3 py-2.5 rounded-lg text-ink text-sm focus:outline-none focus:ring-2 focus:ring-accent-green transition-all"
                        style={{ background: 'var(--c-primary-dark)', border: '0.5px solid var(--c-border)' }}
                        placeholder="Mon entreprise"
                        minLength={2}
                        required={isSignUp}
                      />
                    </div>
                  </div>
                </>
              )}

              <div>
                <label className="block text-ink text-sm font-medium mb-1.5" htmlFor="email">
                  Adresse email
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-label" size={16} />
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full pl-9 pr-3 py-2.5 rounded-lg text-ink text-sm focus:outline-none focus:ring-2 focus:ring-accent-green transition-all"
                    style={{ background: 'var(--c-primary-dark)', border: '0.5px solid var(--c-border)' }}
                    placeholder="vous@exemple.com"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-ink text-sm font-medium mb-1.5" htmlFor="password">
                  Mot de passe
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-label" size={16} />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    className="w-full pl-9 pr-11 py-2.5 rounded-lg text-ink text-sm focus:outline-none focus:ring-2 focus:ring-accent-green transition-all"
                    style={{ background: 'var(--c-primary-dark)', border: '0.5px solid var(--c-border)' }}
                    placeholder="••••••••"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-label hover:text-ink focus:outline-none transition-colors"
                    title={showPassword ? 'Masquer' : 'Afficher'}
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              {!isSignUp && (
                <div className="flex items-center justify-between text-sm">
                  <label className="flex items-center gap-2 text-label cursor-pointer select-none">
                    <input type="checkbox" className="rounded accent-accent-green" />
                    Se souvenir de moi
                  </label>
                  <a href="#" className="text-accent-green hover:underline font-medium transition-colors text-xs">
                    Mot de passe oublié ?
                  </a>
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="btn-primary w-full py-3 flex items-center justify-center gap-2 group disabled:opacity-50 disabled:cursor-not-allowed mt-2"
              >
                {loading ? (
                  <Loader2 size={18} className="animate-spin" />
                ) : (
                  <>
                    <span>{isSignUp ? 'Créer mon compte' : 'Se connecter'}</span>
                    <ArrowRight size={16} className="group-hover:translate-x-0.5 transition-transform" />
                  </>
                )}
              </button>
            </form>

            {/* Toggle */}
            <p className="mt-6 text-center text-label text-sm">
              {isSignUp ? 'Déjà un compte ?' : 'Pas encore de compte ?'}
              {' '}
              <button
                type="button"
                onClick={() => { setIsSignUp(!isSignUp); setError(''); }}
                className="text-accent-green hover:underline font-semibold transition-colors"
              >
                {isSignUp ? 'Se connecter' : 'Créer un compte'}
              </button>
            </p>

            {/* Divider */}
            <div className="mt-6 flex items-center gap-3">
              <div className="flex-1 h-px" style={{ background: 'var(--c-border)' }} />
              <span className="text-label text-xs font-medium">OU</span>
              <div className="flex-1 h-px" style={{ background: 'var(--c-border)' }} />
            </div>

            {/* Google SSO */}
            <button
              type="button"
              className="mt-4 w-full flex items-center justify-center gap-3 px-4 py-2.5 rounded-lg text-ink text-sm font-medium transition-all hover:bg-[var(--c-hover)]"
              style={{ background: 'var(--c-primary-dark)', border: '0.5px solid var(--c-border)' }}
            >
              <svg className="w-4 h-4 flex-shrink-0" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continuer avec Google
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
